"use client";

import { useState } from "react";
import { SummaryCardGrid } from "../shared";
import { MultiLineChart } from "./MultiLineChart";
import { reportsData } from "../../utils/reportsData";

const AgentDetailView = ({ agent }) => {
  const [activeChannelTab, setActiveChannelTab] = useState("All Channels");

  const channelTabs = ["All Channels", "Voice", "Chat", "Email"];

  // Generate agent-specific summary cards
  const generateAgentSummaryCards = (agentData) => [
    {
      label: "Total Interactions",
      count: agentData.interactions.toLocaleString(),
      sublabel: "This month",
      bgColor: "bg-[#DBEAFE]",
      icon: "/icons/headphones.png",
      trend: "up",
      trendValue: "+8.2%",
      timeline: "from last month",
    },
    {
      label: "Average Handle Time",
      count: agentData.avg_handle_time,
      sublabel: "Per interaction",
      bgColor: "bg-[#DCFCE7]",
      icon: "/icons/green-clock.png",
      trend: "down",
      trendValue: "3.1%",
      timeline: "from last month",
    },
    {
      label: "Customer Satisfaction",
      count: `${agentData.csat}/5.0`,
      sublabel: `Based on ${Math.floor(agentData.interactions * 0.7)} ratings`,
      bgColor: "bg-[#F0DFFF]",
      icon: "/icons/emoji.png",
      trend: "up",
      trendValue: "+0.2",
      timeline: "from last month",
    },
    {
      label: "Quality Score",
      count: agentData.quality_score,
      sublabel: "Performance rating",
      bgColor: "bg-[#FFEDD5]",
      icon: "/icons/tick-double.png",
      trend: "up",
      trendValue: "+2%",
      timeline: "from last month",
    },
  ];

  // Generate agent-specific KPI cards
  const generateAgentKPICards = (agentData) => [
    {
      label: "First Call Resolution",
      count: "87.3%",
      bgColor: "bg-[#ffff]",
      icon: "/icons/icon.jpg",
      trend: "up",
      trendValue: "+5.2%",
      timeline: "from last month",
    },
    {
      label: "Response Time",
      count: "32s",
      bgColor: "bg-[#ffff]",
      icon: "/icons/icon.jpg",
      trend: "down",
      trendValue: "12%",
      timeline: "from last month",
    },
    {
      label: "Escalation Rate",
      count: "3.1%",
      bgColor: "bg-[#ffff]",
      icon: "/icons/icon.jpg",
      trend: "down",
      trendValue: "1.8%",
      timeline: "from last month",
    },
    {
      label: "Active Hours",
      count: "7.2h",
      bgColor: "bg-[#ffff]",
      icon: "/icons/icon.jpg",
      trend: "up",
      trendValue: "+0.5h",
      timeline: "from last month",
    },
  ];

  // Generate agent-specific performance data (simulated based on agent performance)
  const generateAgentPerformanceData = (agentData) => {
    const basePerformance = reportsData.channel_performance;
    const performanceMultiplier = agentData.interactions / 400; // Normalize based on interactions

    return basePerformance.map((month) => ({
      month: month.month,
      voice: Math.floor(month.voice * performanceMultiplier * 0.4),
      chat: Math.floor(month.chat * performanceMultiplier * 0.3),
      email: Math.floor(month.email * performanceMultiplier * 0.3),
    }));
  };

  const agentSummaryCards = generateAgentSummaryCards(agent);
  const agentKPICards = generateAgentKPICards(agent);
  const agentPerformanceData = generateAgentPerformanceData(agent);

  return (
    <div className="flex flex-col gap-4">
      <SummaryCardGrid data={agentSummaryCards} />

      {/* Agent Performance Chart */}
      <div className="w-full p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h2 className="text-[13px] pb-3 font-semibold leading-[100%] text-black font-inter">
          {agent.name}'s Channel Performance
        </h2>

        {/* Channel Tabs */}
        <div className="flex p-1 mb-6 space-x-1 bg-white rounded-lg shadow-[4px_4px_20px_0px_#00000040] cursor-pointer w-fit">
          {channelTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveChannelTab(tab)}
              className={`px-4 py-2 text-[13px] cursor-pointer font-medium rounded-md transition-colors ${
                activeChannelTab === tab
                  ? "text-gray-900 shadow-sm bg-gray-100"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Chart */}
        <MultiLineChart
          data={agentPerformanceData}
          activeChannel={activeChannelTab}
          className="mb-6"
        />

        <SummaryCardGrid data={agentKPICards} />
      </div>

      {/* Recent Interactions */}
      <div className="w-full p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h2 className="text-[13px] pb-3 font-semibold leading-[100%] text-black font-inter">
          Recent Interactions
        </h2>
        <div className="space-y-3">
          {[
            {
              type: "Voice Call",
              customer: "John Smith",
              time: "2 hours ago",
              status: "Resolved",
              duration: "4m 32s",
            },
            {
              type: "Chat",
              customer: "Sarah Johnson",
              time: "3 hours ago",
              status: "Active",
              duration: "12m 15s",
            },
            {
              type: "Email",
              customer: "Mike Davis",
              time: "5 hours ago",
              status: "Pending",
              duration: "N/A",
            },
            {
              type: "Voice Call",
              customer: "Lisa Brown",
              time: "6 hours ago",
              status: "Resolved",
              duration: "2m 45s",
            },
          ].map((interaction, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    interaction.type === "Voice Call"
                      ? "bg-blue-100"
                      : interaction.type === "Chat"
                      ? "bg-green-100"
                      : "bg-purple-100"
                  }`}
                >
                  <span className="text-xs font-medium">
                    {interaction.type === "Voice Call"
                      ? "📞"
                      : interaction.type === "Chat"
                      ? "💬"
                      : "📧"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-black">
                    {interaction.customer}
                  </p>
                  <p className="text-xs text-gray-500">
                    {interaction.type} • {interaction.time}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span
                  className={`inline-block px-2 py-1 text-xs rounded-full ${
                    interaction.status === "Resolved"
                      ? "bg-green-100 text-green-800"
                      : interaction.status === "Active"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {interaction.status}
                </span>
                {interaction.duration !== "N/A" && (
                  <p className="mt-1 text-xs text-gray-500">
                    {interaction.duration}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgentDetailView;
